import moment from 'moment';

import {IFieldMeta} from 'entities/metadata';
import {TEntity} from 'modules/data';
import {DATE, DATE_DMY, RESOLVED_FORMATS, TDateFormatType} from 'shared/constants/date-format';

/**
 * Возвращает строку, склоняя слово "час" в зависимости от переданного числительного.
 *
 * @param {number | undefined} hours - Количество часов.
 * @returns {'часов' | 'час' | 'часа'} Строка, содержащая правильное склонением слова "час".
 *
 * @example
 * Примеры использования
 * console.log(getHourString(undefined));   // "часов"
 * console.log(getHourString(1));   // "час"
 * console.log(getHourString(2));   // "часа"
 * console.log(getHourString(5));   // "часов"
 * console.log(getHourString(12));  // "часов"
 * console.log(getHourString(22));  // "часа"
 * console.log(getHourString(123)); // "часа"
 */
export const getHoursString = (hours: number | undefined): 'часов' | 'час' | 'часа' => {
    if (hours === undefined) return 'часов';
    const lastDigit = hours % 10;
    const lastTwoDigits = hours % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
        return 'часов';
    }

    switch (lastDigit) {
    case 1:
        return 'час';
    case 2:
    case 3:
    case 4:
        return 'часа';
    default:
        return 'часов';
    }
};

/**
     * Возвращает количество дней в указанном месяце и году.
     * Если переданный год и месяц совпадают с текущими, возвращает количество дней с начала месяца до текущей даты.
     *
     * @param {number} year - Год, для которого нужно получить количество дней в месяце.
     * @param {number} month - Месяц (от 1 до 12), для которого нужно получить количество дней.
     * @returns {number} Количество дней в месяце.
     * @throws {Error} Если месяц не в диапазоне от 1 до 12.
     *
     * @example
     * Получить количество дней в феврале 2024 года
     * const daysInFebruary2024 = getDaysInMonth(2024, 2);
     * console.log(daysInFebruary2024); // 29
     *
     * @example
     * Получить количество дней с начала текущего месяца до сегодняшнего дня, если текущий месяц - май и год - 2024
     * const currentDays = getDaysInMonth(2024, 5);
     * console.log(currentDays); // Если сегодня 12 мая 2024 года, результат будет 12
    */
export const getDaysInMonth = (year: number, month: number): number => {
    if (month < 1 || month > 12) {
        throw new Error('Месяц должен быть в диапазоне от 1 до 12.');
    }
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    if (year === currentYear && month === currentMonth) {
        const startDate = new Date(year, month - 1, 1);
        const daysPassed = Math.ceil((currentDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        return daysPassed;
    }
    return new Date(year, month, 0).getDate();
};

/**
 * Форматирует дату в указанный формат или возвращает дефис, если дата не передана
 *
 * @param {string | undefined} date - Строка с датой для форматирования. Если не передана или undefined,
 * функция вернёт дефис ('-').
 * @param {TDateFormatType} [format=DATE_DMY] - Формат, в который нужно преобразовать дату.
 * По умолчанию используется формат 'DD.MM.YYYY' (DATE_DMY).
 *
 * @returns {string} Отформатированная дата в виде строки или дефис, если дата не передана.
 *
 * @see {@link TDateFormatType} Доступные форматы даты
 * @see {@link moment} Используется библиотека Moment.js для форматирования
 */
export const formatDate = (date?: string, format: TDateFormatType = DATE_DMY) => (
    date ? moment(date).format(format) : '-'
);

/**
 * Преобразование даты по формату dateFormat, в случае если
 * данные были отправлены в формате string (например 30.09.2019 12:00:00)
 * @param dataRows // данные для таблицы
 * @param metaFields // метаданные, описывающие столбцы таблицы
 *
 * @returns {TEntity[]} Отформатированные данные для таблицы, где преобразованы даты, имеющие формат string на входе.
 */
export const normalizeDateValue = (dataRows: TEntity[] | undefined, metaFields: IFieldMeta[] | undefined) => (
    dataRows?.map(row => {
        const newRow = { ...row };
        metaFields?.forEach(field => {
            if (field.dateFormat && field.type === 'STRING' && row[field.key]) {
                const parsedDate = moment(row[field.key] as string, [DATE_DMY, DATE]);
                if (parsedDate.isValid()) {
                    newRow[field.key] = parsedDate.format(field.dateFormat);
                }
            }
        });
        return newRow;
    }) as TEntity[]
);

/**
 * Преобразует строковое представление даты в объект Moment, используя предопределённые форматы.
 *
 * Функция парсит входную строку даты согласно списку допустимых форматов ({@link RESOLVED_FORMATS}).
 * Парсинг выполняется в строгом режиме (strict mode), что означает, что строка должна точно соответствовать одному из форматов.
 *
 * @param {string} [date] - Строковое представление даты для парсинга. Опциональный параметр.
 * @returns {Moment | null} Объект Moment, если парсинг успешен;
 *                          null, если дата не передана или не соответствует ни одному из допустимых форматов.
 *
 * @example
 * // Успешный парсинг
 * const result = convertToResolvedFormats('22.12.2025');
 * // result → Moment object
 *
 * @example
 * // Пустая или отсутствующая дата
 * const result = convertToResolvedFormats();
 * // result → null
 */
export const convertToResolvedFormats = (date?: string) => {
    if (!date) return null;

    const parsed = moment(date, RESOLVED_FORMATS, true);
    return parsed.isValid() ? parsed : null;
};

